import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, Calendar, Euro, User, Home, Building2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ManualMatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: any;
  onMatch: (rentPeriodId: string) => void;
  isLoading?: boolean;
}

export function ManualMatchDialog({
  open,
  onOpenChange,
  transaction,
  onMatch,
  isLoading = false,
}: ManualMatchDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRentPeriodId, setSelectedRentPeriodId] = useState<string | null>(null);

  // Fetch all unpaid or partially paid rent periods
  const { data: rentPeriods = [], isLoading: loadingPeriods } = useQuery({
    queryKey: ['unpaid-rent-periods'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rent_periods')
        .select(`
          id,
          period_start,
          period_end,
          total_amount,
          paid_amount,
          status,
          leases (
            id,
            tenants (
              id,
              first_name,
              last_name,
              tenant_type,
              company_name
            ),
            properties (
              id,
              name,
              address,
              scis (
                id,
                name
              )
            )
          )
        `)
        .in('status', ['pending', 'partially_paid'])
        .order('period_start', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  // Filter rent periods by search term
  const filteredRentPeriods = useMemo(() => {
    if (!searchTerm) return rentPeriods;

    const term = searchTerm.toLowerCase();
    return rentPeriods.filter((period: any) => {
      const tenant = period.leases?.tenants;
      const property = period.leases?.properties;
      const sci = property?.scis;

      const tenantName = tenant?.tenant_type === 'professional' && tenant?.company_name
        ? tenant.company_name
        : `${tenant?.first_name} ${tenant?.last_name}`;

      return (
        tenantName?.toLowerCase().includes(term) ||
        property?.name?.toLowerCase().includes(term) ||
        property?.address?.toLowerCase().includes(term) ||
        sci?.name?.toLowerCase().includes(term) ||
        period.total_amount?.toString().includes(term)
      );
    });
  }, [rentPeriods, searchTerm]);

  const handleMatch = () => {
    if (selectedRentPeriodId) {
      onMatch(selectedRentPeriodId);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Rapprochement manuel</DialogTitle>
          <DialogDescription>
            Sélectionnez la période de loyer correspondant à cette transaction.
          </DialogDescription>
        </DialogHeader>

        {/* Transaction Summary */}
        {transaction && (
          <div className="p-4 bg-muted/50 rounded-lg space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Building2 className="h-4 w-4" />
              {transaction.bank_accounts?.scis?.name}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 text-xl font-bold text-green-600">
                <Euro className="h-5 w-5" />
                +{transaction.amount?.toFixed(2)} €
              </div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                {format(new Date(transaction.transaction_date), 'dd/MM/yyyy', { locale: fr })}
              </div>
            </div>
            {transaction.description && (
              <p className="text-sm text-muted-foreground">{transaction.description}</p>
            )}
          </div>
        )}

        {/* Search */}
        <div className="space-y-2">
          <Label htmlFor="search">Rechercher une période de loyer</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="search"
              placeholder="Locataire, bien, SCI, montant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Rent Periods List */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {loadingPeriods ? (
            <div className="text-center py-8 text-muted-foreground">
              Chargement des périodes de loyer...
            </div>
          ) : filteredRentPeriods.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Aucune période de loyer impayée trouvée.
            </div>
          ) : (
            filteredRentPeriods.map((period: any) => {
              const tenant = period.leases?.tenants;
              const property = period.leases?.properties;
              const sci = property?.scis;
              const tenantName = tenant?.tenant_type === 'professional' && tenant?.company_name
                ? tenant.company_name
                : `${tenant?.first_name} ${tenant?.last_name}`;

              const isSelected = selectedRentPeriodId === period.id;
              const amountDiff = (transaction?.amount || 0) - period.total_amount;

              return (
                <Card
                  key={period.id}
                  className={`cursor-pointer transition-all ${
                    isSelected ? 'border-2 border-primary bg-primary/5' : 'hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedRentPeriodId(period.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-semibold">{tenantName}</span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Home className="h-3 w-3" />
                            {property?.name || property?.address}
                          </div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Building2 className="h-3 w-3" />
                            {sci?.name}
                          </div>
                        </div>

                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            {format(new Date(period.period_start), 'MMMM yyyy', { locale: fr })}
                          </div>
                          <Badge variant={period.status === 'pending' ? 'destructive' : 'secondary'}>
                            {period.status === 'pending' ? 'Impayé' : 'Partiellement payé'}
                          </Badge>
                        </div>
                      </div>

                      <div className="text-right space-y-1">
                        <div className="font-bold text-lg">{period.total_amount?.toFixed(2)} €</div>
                        {Math.abs(amountDiff) > 0.01 && (
                          <div className={`text-sm ${
                            Math.abs(amountDiff) < 0.01 ? 'text-green-600' : 'text-orange-600'
                          }`}>
                            Diff: {amountDiff.toFixed(2)} €
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
          >
            Annuler
          </Button>
          <Button
            onClick={handleMatch}
            disabled={!selectedRentPeriodId || isLoading}
            className="flex-1"
          >
            {isLoading ? 'Rapprochement...' : 'Confirmer le rapprochement'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
